import { useNavigate } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { m } from "framer-motion";
import type { ReactNode } from "react";
import { Skeleton, StatCard } from "@rumbledirect/ui-v2";
import { useCurrentUser } from "@/currentUser/CurrentUserProvider";
import { hasPermission } from "@/currentUser/permissions";
import { StatusDistributionPanel } from "@/dashboard/StatusDistributionPanel";
import { UnreadDossiersPanel } from "@/dashboard/UnreadDossiersPanel";
import { WorkQueuePanel } from "@/dashboard/WorkQueuePanel";
import { groupPanelRows } from "@/dashboard/groupPanelRows";
import { greeting } from "@/dashboard/greeting";
import {
  DEFAULT_DASHBOARD_KPIS_LAYOUT,
  type DashboardPanel,
} from "@/dossiers/dashboardKpisLayout";
import { useDashboardKpisLayout } from "@/dossiers/useDashboardKpisLayout";
import { DOSSIER_KPI_TILE_META } from "@/dossiers/dossierKpiTiles";
import { useDossierStats } from "@/dossiers/useDossierStats";
import {
  AnimatedNumber,
  staggerContainer,
  staggerItem,
  useMotionInitial,
} from "@rumbledirect/ui-v2/motion";
import {
  DEFAULT_DOSSIERS_SEARCH,
  type DossiersSearch,
} from "@/dossiers/dossiersSearch";

// Placeholder for the Dashboard section. Content-only: the authenticated
// AppShell owns the single <main> landmark and page layout container.
export const Route = createFileRoute("/_authenticated/dashboard")({
  component: DashboardPage,
});

/**
 * The component each configured panel renders as. An identifier without a
 * card (e.g. one written by a newer front-end) resolves to nothing; `null`
 * is a valid render and leaves the rest of the dashboard untouched.
 */
const PANEL_COMPONENTS: Record<DashboardPanel, (() => ReactNode) | null> = {
  statusDistribution: StatusDistributionPanel,
  unreadDossiers: UnreadDossiersPanel,
  workQueue: WorkQueuePanel,
};

function DashboardPage(): ReactNode {
  const motionInitial = useMotionInitial("hidden");
  const currentUser = useCurrentUser();
  const navigate = useNavigate();

  // Single lightweight KPI-counts query, mirroring the dossiers route's
  // "Kerncijfers" tiles: the dashboard's stats are always-available one-tap
  // shortcuts into /dossiers, rather than a readout of any local filter
  // state.
  const canReadDossiers = hasPermission(currentUser, "admin_dossier_read");
  const statsQuery = useDossierStats(canReadDossiers);
  const isPending = statsQuery.isPending || statsQuery.isError;

  // Which tiles to show, and in what order: affiliate-configured, same as the
  // dossiers overview's own KPI set (see `useDashboardKpisLayout`) — falls
  // back to its own default while loading, when unconfigured (a 404 is a
  // normal state) or when the request otherwise fails (a transient error
  // here must not cost the dashboard its always-available shortcuts).
  const kpisLayout = useDashboardKpisLayout();
  const kpis = canReadDossiers
    ? (kpisLayout.data?.kpis ?? DEFAULT_DASHBOARD_KPIS_LAYOUT.kpis)
    : [];
  const panels = (
    kpisLayout.data?.panels ?? DEFAULT_DASHBOARD_KPIS_LAYOUT.panels
  ).filter((panel) =>
    panel === "workQueue"
      ? hasPermission(currentUser, "admin_work_queue_task_read")
      : canReadDossiers,
  );

  const goToDossiers = (search: Partial<DossiersSearch>): void => {
    void navigate({
      to: "/dossiers",
      search: { ...DEFAULT_DOSSIERS_SEARCH, ...search },
    });
  };

  const heading = currentUser.firstName
    ? `${greeting()}, ${currentUser.firstName}`
    : greeting();

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">
          {heading}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Je werk in één oogopslag.
        </p>
      </div>

      {canReadDossiers && (
        <m.div
          role="group"
          aria-label="Kerncijfers"
          variants={staggerContainer}
          initial={motionInitial}
          animate="visible"
          className="grid gap-3 sm:grid-cols-3"
        >
          {kpisLayout.isPending
            ? // Reserves the row's footprint while the tile set resolves, sized
              // 1:1 to the default tile count, so the page does not jump once
              // it arrives.
              DEFAULT_DASHBOARD_KPIS_LAYOUT.kpis.map((kpi) => (
                <Skeleton
                  key={kpi}
                  aria-hidden="true"
                  className="h-24 w-full"
                />
              ))
            : kpis.map((kpi) => {
                const tile = DOSSIER_KPI_TILE_META[kpi];
                const onClick = (): void => {
                  if (kpi === "unread") {
                    goToDossiers({ unread: true });
                  } else if (kpi === "actionRequired") {
                    goToDossiers({ actionRequired: true });
                  } else if (kpi === "mine") {
                    goToDossiers({ assigneeId: currentUser.userId });
                  } else {
                    goToDossiers({});
                  }
                };

                return (
                  <m.div key={kpi} variants={staggerItem}>
                    <StatCard
                      label={tile.label}
                      icon={tile.icon}
                      value={
                        isPending ? (
                          "–"
                        ) : (
                          <AnimatedNumber value={tile.value(statsQuery.data)} />
                        )
                      }
                      onClick={onClick}
                      className="w-full"
                    />
                  </m.div>
                );
              })}
        </m.div>
      )}

      {/* Each panel owns its own data fetching, loading, empty and error
          state, so one failing panel never blanks the dashboard. Consecutive
          `unreadDossiers`/`workQueue` panels render side by side above `xl`:
          together they form one "your open work" group, and each is compact
          enough at half the container width. Any other pair, and either
          panel alone, still gets the full width. */}
      {groupPanelRows(panels).map((row) => (
        <div
          key={row.join("+")}
          className={row.length > 1 ? "grid gap-5 xl:grid-cols-2" : undefined}
        >
          {row.map((panel) => {
            const PanelComponent = PANEL_COMPONENTS[panel];

            return PanelComponent ? <PanelComponent key={panel} /> : null;
          })}
        </div>
      ))}
    </div>
  );
}
